import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../api';
import { useGame } from '../context/GameContext';

const DEFENSE_INFO = {
  turret:  { icon: '🔫', label: 'Turret', desc: 'High firepower, targets attackers' },
  shield:  { icon: '🛡', label: 'Shield Gen', desc: 'Absorbs incoming damage' },
  mine:    { icon: '💣', label: 'Mine Field', desc: 'Damages on approach' },
  jammer:  { icon: '📡', label: 'Jammer', desc: 'Reduces enemy accuracy' },
};

function CombatPanel() {
  const { refreshInventory } = useGame();
  const [fleets, setFleets] = useState([]);
  const [battles, setBattles] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [defenses, setDefenses] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [ships, setShips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [newFleetName, setNewFleetName] = useState('');
  const [newFleetSector, setNewFleetSector] = useState('');
  const [expandedBattle, setExpandedBattle] = useState(null);
  const [battleDetail, setBattleDetail] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [fleetRes, battleRes, secRes, shipsRes] = await Promise.all([
        api.getCombatFleets(),
        api.getBattles(),
        api.getSectors(),
        api.getFleet(),
      ]);
      setFleets(fleetRes.data.fleets || []);
      setBattles(battleRes.data.battles || []);
      setSectors(secRes.data.sectors || []);
      setShips((shipsRes.data.ships || []).filter(s => !s.fleet_id && s.status === 'idle'));
    } catch (err) {
      console.error('Load combat error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadDefenses = async (sectorId) => {
    try {
      setSelectedSector(sectorId);
      const { data } = await api.getDefenses(sectorId);
      setDefenses(data.defenses || []);
    } catch (err) {
      console.error('Load defenses error:', err);
    }
  };

  const loadBattleDetail = async (battleId) => {
    if (expandedBattle === battleId) {
      setExpandedBattle(null);
      setBattleDetail(null);
      return;
    }
    try {
      const { data } = await api.getBattleDetail(battleId);
      setBattleDetail(data.battle);
      setExpandedBattle(battleId);
    } catch (err) {
      console.error('Load battle detail error:', err);
    }
  };

  const handleCreateFleet = async () => {
    if (!newFleetName || !newFleetSector) return;
    try {
      await api.createCombatFleet(newFleetName, Number(newFleetSector));
      setActionMsg('Fleet created!');
      setNewFleetName(''); setNewFleetSector('');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Create failed');
    }
  };

  const handleAddShip = async (fleetId, shipId) => {
    try {
      await api.addShipToFleet(fleetId, shipId);
      setActionMsg('Ship added to fleet.');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Add failed');
    }
  };

  const handleRemoveShip = async (shipId) => {
    try {
      await api.removeShipFromFleet(shipId);
      setActionMsg('Ship removed from fleet.');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Remove failed');
    }
  };

  const handleRepairShip = async (shipId) => {
    try {
      await api.repairShip(shipId);
      setActionMsg('Ship repaired!');
      await loadData();
      refreshInventory();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Repair failed');
    }
  };

  const handleMove = async (fleetId, targetSectorId) => {
    try {
      await api.moveFleet(fleetId, targetSectorId);
      setActionMsg('Fleet moving...');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Move failed');
    }
  };

  const handleAttack = async (fleetId) => {
    try {
      await api.attackWithFleet(fleetId);
      setActionMsg('Battle started!');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Attack failed');
    }
  };

  const handleRetreat = async (fleetId) => {
    try {
      await api.retreatFleet(fleetId);
      setActionMsg('Fleet retreated.');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Retreat failed');
    }
  };

  const handleBuildDefense = async (platformType) => {
    if (!selectedSector) return;
    try {
      await api.buildDefense(selectedSector, platformType);
      setActionMsg('Defense built!');
      await loadDefenses(selectedSector);
      refreshInventory();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Build failed');
    }
  };

  const sectorName = (id) => {
    const s = sectors.find(sec => sec.id === id);
    return s ? s.name : `Sector #${id}`;
  };

  const hpPercent = (hp, max) => max > 0 ? Math.round((hp / max) * 100) : 0;
  const hpColor = (pct) => pct > 60 ? '#4ade80' : pct > 25 ? '#f59e0b' : '#ef4444';

  if (loading) return <div className="panel-loading">Loading combat...</div>;

  return (
    <div className="panel-container combat-panel">
      <div className="panel-header">
        <h2>⚔ Combat</h2>
      </div>

      {actionMsg && <div className="action-msg">{actionMsg}<button className="msg-dismiss" onClick={() => setActionMsg('')}>×</button></div>}

      <div className="combat-layout">
        {/* Fleets */}
        <div className="combat-section">
          <h3 className="section-title"><span className="title-accent">⚑</span> Combat Fleets</h3>

          {/* Create Fleet */}
          <div className="section build-section compact">
            <div className="form-row">
              <input
                type="text" placeholder="Fleet name..." value={newFleetName}
                onChange={e => setNewFleetName(e.target.value)} maxLength={30}
              />
              <select value={newFleetSector} onChange={e => setNewFleetSector(e.target.value)}>
                <option value="">Sector...</option>
                {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
              <button className="btn btn-glow" onClick={handleCreateFleet}>Create Fleet</button>
            </div>
          </div>

          {fleets.length === 0 && <p className="hud-empty">No combat fleets. Create one above.</p>}
          {fleets.map(f => (
            <div key={f.id} className="section fleet-card">
              <div className="fleet-header">
                <div className="fleet-title">
                  <span className="fleet-name">{f.name}</span>
                  <span className={`badge status-${f.status}`}>{f.status.toUpperCase()}</span>
                </div>
                <span className="fleet-location">📍 {sectorName(f.sector_id)}</span>
              </div>

              {/* Ships in fleet */}
              {f.ships && f.ships.length > 0 && (
                <div className="fleet-ships">
                  {f.ships.map(s => {
                    const pct = hpPercent(s.hp, s.max_hp);
                    return (
                      <div key={s.id} className="fleet-ship-item">
                        <div className="ship-info-row">
                          <span className="ship-name">{s.name}</span>
                          <span className="ship-hp" style={{color: hpColor(pct)}}>HP {s.hp}/{s.max_hp}</span>
                        </div>
                        <div className="progress-track sm">
                          <div className="progress-fill" style={{ width: `${pct}%`, backgroundColor: hpColor(pct) }} />
                        </div>
                        <div className="ship-actions">
                          {s.hp < s.max_hp && (
                            <button className="btn btn-sm btn-repair" onClick={() => handleRepairShip(s.id)}>
                              🔧 Repair ({(s.max_hp - s.hp) * 2}cr)
                            </button>
                          )}
                          <button className="btn btn-sm btn-danger" onClick={() => handleRemoveShip(s.id)}>Remove</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add ship */}
              {ships.length > 0 && f.status === 'idle' && (
                <div className="add-ship-row">
                  <select id={`add-ship-${f.id}`} defaultValue="">
                    <option value="" disabled>Add ship...</option>
                    {ships.filter(s => s.sector_id === f.sector_id).map(s => (
                      <option key={s.id} value={s.id}>{s.name} (HP:{s.hp}/{s.max_hp})</option>
                    ))}
                  </select>
                  <button className="btn btn-sm" onClick={() => {
                    const sel = document.getElementById(`add-ship-${f.id}`);
                    if (sel.value) handleAddShip(f.id, Number(sel.value));
                  }}>Add</button>
                </div>
              )}

              {/* Fleet Actions */}
              <div className="fleet-actions">
                {f.status === 'idle' && f.ships?.length > 0 && (
                  <>
                    <select id={`move-${f.id}`} defaultValue="">
                      <option value="" disabled>Move to...</option>
                      {sectors.filter(s => s.id !== f.sector_id).map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    <button className="btn btn-sm" onClick={() => {
                      const sel = document.getElementById(`move-${f.id}`);
                      if (sel.value) handleMove(f.id, Number(sel.value));
                    }}>Move</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleAttack(f.id)}>⚔ Attack</button>
                  </>
                )}
                {f.status === 'combat' && (
                  <button className="btn btn-sm btn-danger" onClick={() => handleRetreat(f.id)}>🏳 Retreat</button>
                )}
                {f.status === 'moving' && f.arrives_at && (
                  <span className="eta">⏱ ETA: {new Date(f.arrives_at).toLocaleTimeString()}</span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Defenses */}
        <div className="combat-section">
          <h3 className="section-title"><span className="title-accent">🛡</span> Sector Defenses</h3>
          <div className="form-field">
            <select value={selectedSector || ''} onChange={e => { if (e.target.value) loadDefenses(Number(e.target.value)); }}>
              <option value="">Select sector...</option>
              {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>

          {selectedSector && (
            <>
              <div className="defense-build-grid">
                {Object.entries(DEFENSE_INFO).map(([key, info]) => (
                  <div key={key} className="defense-build-card" onClick={() => handleBuildDefense(key)}>
                    <span className="defense-icon">{info.icon}</span>
                    <span className="defense-label">{info.label}</span>
                    <span className="defense-desc">{info.desc}</span>
                  </div>
                ))}
              </div>
              {defenses.length === 0 && <p className="hud-empty">No defenses in this sector.</p>}
              {defenses.map(d => {
                const pct = hpPercent(d.hp, d.max_hp);
                return (
                  <div key={d.id} className="section defense-card">
                    <div className="defense-header">
                      <span className="defense-icon">{DEFENSE_INFO[d.type]?.icon || '⚙'}</span>
                      <div className="defense-info">
                        <span className="defense-type">{DEFENSE_INFO[d.type]?.label || d.type}</span>
                        <span className="defense-meta">Lv.{d.level} • FP: {d.firepower}</span>
                      </div>
                    </div>
                    <div className="defense-hp-row">
                      <span className="hp-label" style={{color: hpColor(pct)}}>HP {d.hp}/{d.max_hp}</span>
                      <div className="progress-track sm">
                        <div className="progress-fill" style={{ width: `${pct}%`, backgroundColor: hpColor(pct) }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Battle Log */}
        <div className="combat-section">
          <h3 className="section-title"><span className="title-accent">📜</span> Battle History</h3>
          {battles.length === 0 && <p className="hud-empty">No battles yet.</p>}
          {battles.map(b => {
            const isWin = b.winner_id === b.attacker_id;
            const statusClass = b.status === 'active' ? 'status-active' : (isWin ? 'status-win' : 'status-loss');
            return (
              <div key={b.id} className="section battle-card" onClick={() => loadBattleDetail(b.id)}>
                <div className="battle-header">
                  <span className="battle-sector">📍 {sectorName(b.sector_id)}</span>
                  <span className={`badge ${statusClass}`}>{b.status === 'active' ? 'IN PROGRESS' : b.status.toUpperCase()}</span>
                  <span className="battle-round">Round {b.round}/{b.max_rounds}</span>
                </div>
                {b.winner_id && (
                  <div className={`battle-result ${isWin ? 'win' : 'loss'}`}>
                    {isWin ? '🏆 Victory (Attacker)' : '💀 Defeat (Defender wins)'}
                  </div>
                )}

                {/* Expanded Battle Detail */}
                {expandedBattle === b.id && battleDetail && (
                  <div className="battle-detail" onClick={e => e.stopPropagation()}>
                    <h4>Battle Log</h4>
                    <div className="battle-log">
                      {battleDetail.log && battleDetail.log.length > 0 ? (
                        battleDetail.log.map((entry, i) => (
                          <div key={i} className="log-entry">
                            <span className="log-round">R{entry.round || i + 1}</span>
                            <span className="log-text">{entry.message || entry.event || JSON.stringify(entry)}</span>
                          </div>
                        ))
                      ) : (
                        <p className="hud-empty">No detailed log available.</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CombatPanel;
